import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Select, RadioList, TFileWithType } from '@treezor/design-system';

import { SanctionEmbargoQuestionaire } from '@/features/user/components/CreateUser/SanctionEmbargoQuestionaire';
import { ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';
import { USResident } from '@/features/user/components/CreateUser/USResident';
import { useAddTaxResidences } from '@/features/user/hooks/createUser/useTaxResidences';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { ContentLayout, CustomColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { useCreateUserKycDocuments } from '@/features/user/hooks/createUser/useCreateUserKycDocuments';
import { CreateUserKycDocuments } from '@/features/user/components/CreateUser/CreateUserKycDocuments';
import { CreateUserTaxResidences } from '@/features/user/components/CreateUser/CreateUserTaxResidences';
import { ICreateUserPartProps } from '@/features/user/store/createUser.types';

export function CreateUserBusinessVerificationDetails({ stepsLength }: ICreateUserPartProps) {
  const { t } = useTranslation();

  const { handleSubmit, watches, user, handleValueChange, shouldBeUSResident } = useCreateBusinessUser({
    legalAnnualTurnOver: undefined,
    legalNetIncomeRange: undefined,
    isOnStockExchange: undefined,
    sourceOfFunds: undefined,
    legalNumberOfEmployeeRange: undefined,
    specifiedUSPerson: 0,
    entitySanctionsQuestionnaire: undefined,
    activityOutsideEu: 0,
    economicSanctions: 0,
    residentCountriesSanctions: 0,
    involvedSanctions: 0,
    taxResidences: undefined,
    kycDocuments: undefined,
  });

  const { isTaxResidenceDisabled } = useAddTaxResidences();

  const { isKycDocumentsInvalid } = useCreateUserKycDocuments();

  const {
    legalAnnualTurnOverOptions,
    legalNetIncomeRangeOptions,
    legalNumberOfEmployeeRangeOptions,
    sourceOfFundsOptions,
  } = useFieldOptions();

  const specifiedUSPersonWatch = watches['specifiedUSPerson'];
  const entitySanctionsQuestionnaireWatch = watches['entitySanctionsQuestionnaire'];
  const taxResidencesWatch = watches['taxResidences'];
  const kycDocumentsWatch = watches['kycDocuments'];

  const isFormDisabled = useMemo(
    () => isTaxResidenceDisabled(taxResidencesWatch, true) || isKycDocumentsInvalid(kycDocumentsWatch),
    [isTaxResidenceDisabled, taxResidencesWatch, isKycDocumentsInvalid, kycDocumentsWatch],
  );

  return (
    <div className="flex flex-col gap-6">
      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.situation')}>
        <CustomColumns columns={[1, 1]}>
          <RadioList
            label={t('pages.dashboard.createUser.form.legalAnnualTurnOver')}
            options={legalAnnualTurnOverOptions}
            value={watches['legalAnnualTurnOver']}
            onChange={(value: string) => handleValueChange(value, 'legalAnnualTurnOver')}
            className="pt-2 gap-3"
          />
          <div className="flex flex-col gap-6">
            <RadioList
              label={t('pages.dashboard.createUser.form.legalNetIncomeRanges.title')}
              options={legalNetIncomeRangeOptions}
              value={watches['legalNetIncomeRange']}
              onChange={(value: string) => handleValueChange(value, 'legalNetIncomeRange')}
              className="pt-2 gap-3"
            />

            <RadioList
              label={t('pages.currentProfiles.profile.financial.isOnStockExchange')}
              options={DashboardHelpers.getBooleanRadioValues().map((option) => ({
                ...option,
                value: `${option.value}`,
              }))}
              value={watches['isOnStockExchange']}
              onChange={(value: string) => handleValueChange(value, 'isOnStockExchange')}
              className="pt-2 gap-3"
              direction="row"
            />
          </div>
        </CustomColumns>
        <CustomColumns columns={[1, 1]}>
          <Select
            label={t('pages.dashboard.createUser.form.sourceOfFunds.title')}
            options={sourceOfFundsOptions}
            defaultValue={user?.sourceOfFunds}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'sourceOfFunds')}
            showDelete
          />
          <Select
            label={t('pages.dashboard.createUser.form.legalNumberOfEmployee')}
            options={legalNumberOfEmployeeRangeOptions}
            defaultValue={user?.legalNumberOfEmployeeRange}
            noOptionsComponent={<CreateUserEmptyComponent />}
            onChange={(value: string) => handleValueChange(value, 'legalNumberOfEmployeeRange')}
            showDelete
          />
        </CustomColumns>
        <SanctionEmbargoQuestionaire
          defaultValue={{
            entitySanction: entitySanctionsQuestionnaireWatch,
            activityOutsideEu: watches?.['activityOutsideEu']?.toString(),
            economicSanctions: watches?.['economicSanctions']?.toString(),
            residentCountriesSanctions: watches?.['residentCountriesSanctions']?.toString(),
            involvedSanctions: watches?.['involvedSanctions']?.toString(),
          }}
          handleValueChange={handleValueChange}
        />
      </ContentLayout>

      <ContentLayout title={t('pages.currentProfiles.profile.taxResidence.cardTitle')}>
        <USResident
          title={t('pages.dashboard.createUser.form.isCompanyUsTaxResident')}
          description={t('pages.dashboard.createUser.form.usCompanyTaxResidentDescription')}
          value={specifiedUSPersonWatch.toString()}
          handleValuesChange={(changes) => changes.map(({ value, key }) => handleValueChange(value, key))}
          taxResidences={taxResidencesWatch}
          shouldShowWarning={shouldBeUSResident(['country', 'secondaryCountry']) && specifiedUSPersonWatch !== '1'}
        />
        <CreateUserTaxResidences
          taxResidences={taxResidencesWatch}
          defaultCountry={user?.country}
          onUpdate={(value: ITaxResidenceCreate[]) => handleValueChange(value, 'taxResidences')}
          isBusiness
        />
      </ContentLayout>

      <ContentLayout title={t('pages.dashboard.createUser.form.blocks.documents')} hasDivider={false}>
        <CreateUserKycDocuments
          handleFileChange={(files: TFileWithType[]) => handleValueChange(files, 'kycDocuments')}
          defaultFiles={user?.kycDocuments}
        />
      </ContentLayout>
      <CreateUserFooter
        onNext={handleSubmit}
        isNextDisabled={isFormDisabled}
        onPrev={handleSubmit}
        stepsLength={stepsLength}
      />
    </div>
  );
}
